import { useMediaQuery } from '@material-ui/core'
import { BaseThemeType, styled, Typography } from 'opensolar-ui'
import { Fragment, useMemo } from 'react'
import { CheckoutFinancingPricingOption } from '../types'
import { formatCurrencyWithSymbol } from '../utils'

const ProductDetailsRow = styled('div')<{ isMobile: boolean }>(({ isMobile }: { isMobile: boolean }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: isMobile ? 'center' : 'flex-start',
  alignItems: 'stretch',
  width: '100%',
}))

const ProductDetailDisplay = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'stretch',
  width: '100%',
  textAlign: 'center',
}))

const ProductDetailTitle = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  lineHeight: '16px',
  fontSize: '12px',
  fontWeight: 500,
  color: theme.palette.neutralGrey[900],
}))

const ProductDetailTitleHighlighted = styled(ProductDetailTitle)(({ theme }: { theme: BaseThemeType }) => ({
  fontWeight: theme.typography.fontWeightBold,
}))

const ProductDetailMainFigure = styled(Typography)(() => ({
  fontWeight: 600,
}))

const ProductDetailMainFigureHighlighted = styled(ProductDetailMainFigure)(({ theme }: { theme: BaseThemeType }) => ({
  color: theme.palette.blue[800],
}))

const SpacedDivider = styled('div')<{ isMobile: boolean }>(
  ({ theme, isMobile }: { theme: BaseThemeType; isMobile: boolean }) => ({
    backgroundColor: theme.palette.neutralGrey[600],
    width: '2px',
    height: isMobile ? '24px' : '48px',
    marginRight: isMobile ? '8px' : '32px',
    marginLeft: isMobile ? '8px' : '32px',
  })
)

const formatTermValue = (value: number): string => {
  const years = Math.floor(value / 12)
  const yearsSuffix = years === 1 ? 'year' : 'years'
  const months = value % 12
  const monthsSuffix = months === 1 ? 'month' : 'months'

  return months === 0 ? `${years} ${yearsSuffix}` : `${years} ${yearsSuffix} ${months} ${monthsSuffix}`
}

const formatHeadlineValue = (
  value: string | number,
  valueFormat: 'string' | 'number' | 'currency' | 'percentage' | 'term' | null,
  currencySymbol: string,
  significantDigits: number | null
): string => {
  const places = typeof significantDigits === 'number' ? significantDigits : 0

  if (valueFormat === 'currency') {
    return formatCurrencyWithSymbol(value, currencySymbol, undefined, places)
  } else if (valueFormat === 'number') {
    return Number(value).toFixed(places)
  } else if (valueFormat === 'percentage') {
    return `${Number(value).toFixed(places)}%`
  } else if (valueFormat === 'term') {
    return formatTermValue(Number(value))
  }

  return String(value)
}

type PropTypes = {
  pricingOption: Pick<
    CheckoutFinancingPricingOption,
    'term' | 'headline_figures' | 'currency_symbol' | 'apr' | 'monthly_payment' | 'total_repayable'
  >
}

const PricingOptionDetailsRow = ({ pricingOption }: PropTypes) => {
  const { term, headline_figures, currency_symbol, apr, monthly_payment, total_repayable } = pricingOption
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))
  const termInYears = useMemo(() => formatTermValue(term), [term])

  if (headline_figures && headline_figures?.length > 0) {
    return (
      <ProductDetailsRow isMobile={isMobile}>
        {headline_figures.map((figure, index) => {
          const isLast = index === headline_figures!.length - 1
          const formattedValue = formatHeadlineValue(
            figure.value,
            figure.value_format,
            currency_symbol,
            figure.significant_digits
          )
          const ProductDetailTitleComponent = figure.highlight_title
            ? ProductDetailTitleHighlighted
            : ProductDetailTitle
          const ProductDetailMainFigureComponent = figure.highlight_value
            ? ProductDetailMainFigureHighlighted
            : ProductDetailMainFigure

          return (
            <Fragment key={index}>
              <ProductDetailDisplay>
                <ProductDetailTitleComponent textVariant={isMobile ? 'body1' : 'body2'}>
                  {figure.title}
                </ProductDetailTitleComponent>
                <ProductDetailMainFigureComponent textVariant={isMobile ? 'h6' : 'h5'}>
                  {formattedValue}
                </ProductDetailMainFigureComponent>
              </ProductDetailDisplay>
              {!isLast && <SpacedDivider isMobile={isMobile} />}
            </Fragment>
          )
        })}
      </ProductDetailsRow>
    )
  }

  return (
    <ProductDetailsRow isMobile={isMobile}>
      <ProductDetailDisplay>
        <ProductDetailTitle textVariant={isMobile ? 'body1' : 'body2'}>APR</ProductDetailTitle>
        <ProductDetailMainFigure textVariant={isMobile ? 'h6' : 'h5'}>{apr}%</ProductDetailMainFigure>
      </ProductDetailDisplay>
      <SpacedDivider isMobile={isMobile} />
      {monthly_payment && (
        <>
          <ProductDetailDisplay>
            <ProductDetailTitle textVariant={isMobile ? 'body1' : 'body2'}>Monthly Payment</ProductDetailTitle>
            <ProductDetailMainFigureHighlighted textVariant={isMobile ? 'h6' : 'h5'}>
              {formatCurrencyWithSymbol(monthly_payment, currency_symbol, undefined, 0)}
            </ProductDetailMainFigureHighlighted>
          </ProductDetailDisplay>
          <SpacedDivider isMobile={isMobile} />
        </>
      )}
      <ProductDetailDisplay>
        <ProductDetailTitle textVariant={isMobile ? 'body1' : 'body2'}>Total Repayable</ProductDetailTitle>
        <ProductDetailMainFigure textVariant={isMobile ? 'h6' : 'h5'}>
          {formatCurrencyWithSymbol(total_repayable, currency_symbol, undefined, 0)}
        </ProductDetailMainFigure>
      </ProductDetailDisplay>
      <SpacedDivider isMobile={isMobile} />
      <ProductDetailDisplay>
        <ProductDetailTitle textVariant={isMobile ? 'body1' : 'body2'}>Loan Term</ProductDetailTitle>
        <ProductDetailMainFigure textVariant={isMobile ? 'h6' : 'h5'}>{termInYears}</ProductDetailMainFigure>
      </ProductDetailDisplay>
    </ProductDetailsRow>
  )
}
export default PricingOptionDetailsRow
